<!--
 * @Descripttion: 已完结订单
 * @Date: 2024-08-06 13:58:47
-->
<template>
  <div class="finished-order-list">
    <xrk-table
      ref="xrkTableRef"
      :getListAjax="getListAjax"
      :searchConfig="searchConfig"
      :tableConfig="tableConfig"
    ></xrk-table>
  </div>
</template>

<script lang="tsx" setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  XrkTable,
  XrkTableProps,
  useTableColumn,
  BaseButton,
  XrkTableRef,
  XrkMessage,
  XrkMessageBox
} from 'xrk-components';

import { payedOrderPageList } from '../data-source/apis';
import {
  orderNoCol,
  projectNameCol,
  projectNoCol,
  taxCollectionPatternCol,
  entpNameCol,
  supplierNameCol,
  orderTimeCol,
  orderTypeCol,
  payTypeCol,
  payPassagewayCol,
  orderPriceCol,
  orderFinishTimeCol,
  deductIndividualTaxAmountCol,
  paidOrderAmountCol
} from '../fields/columns';
import {
  orderNoSearch,
  projectNameSearch,
  projectNoSearch,
  taxCollectionPatternSearch,
  entpNameSearch,
  supplierNameSearch,
  orderTypeSearch,
  orderTimeAtSearch,
  payTypeSearch,
  payPassagewayNameSearch,
  orderFinishTimeSearch
} from '../fields/searchs';

import emitter from '../utils/event-bus';
import {
  batchDownLoadOrderPayDetail,
  batchDownLoadReceipt,
  downLoadOrderPayDetail,
  downLoadReceipt
} from '@/common/hooks/order/order-download';

const xrkTableRef = ref<XrkTableRef>();
const router = useRouter();
const route = useRoute();
const onRefreshTabel = () => {
  xrkTableRef.value?.initCurrentList();
};

const selectionVal = computed(() => {
  return xrkTableRef.value?.getSelectionRows();
});

onMounted(() => {
  emitter.on('refresh-order-tabel', () => {
    onRefreshTabel();
  });
});

const taxType = computed(() => {
  return route.meta.taxType as number;
});

// 搜索项
const searchConfig = ref<XrkTableProps['searchConfig']>({
  searchBtn: {
    show: true,
    text: '搜索'
  },
  resetBtn: {
    show: true,
    text: '重置',
    beforeReset: () => {
      // 清除路由上的查询参数
      if (route.query.orderNo) {
        router.replace({
          query: { force: Date.now() }
        });
      }
    }
  },
  searchColumns: [
    { ...orderNoSearch, defaultValue: route.query.orderNo },
    projectNameSearch,
    projectNoSearch,
    payPassagewayNameSearch,
    taxCollectionPatternSearch,
    entpNameSearch,
    taxType.value === 1 && supplierNameSearch,
    orderTypeSearch,
    orderTimeAtSearch,
    orderFinishTimeSearch,
    payTypeSearch
  ].filter(Boolean),
  otherComponents: [
    {
      render() {
        return (
          <>
            <BaseButton
              v-resource={[['已完结订单_下载支付详情']]}
              type="primary2"
              onClick={async () => {
                const { payPassagewayCode, orderType } = searchedParams.value;

                if (!payPassagewayCode || +orderType !== 1) {
                  XrkMessageBox.alert(
                    '当前状态不支持批量下载，请先选择【支付通道】并选择订单类型为【项目服务费】点击搜索后操作'
                  );
                  return;
                }
                if (!selectionVal.value.length) {
                  XrkMessage.error('请选择一条数据');
                  return;
                }
                await batchDownLoadOrderPayDetail({
                  payPassagewayCode:
                    xrkTableRef.value.getSearchValue().payPassagewayCode,
                  aspOrderIdList: selectionVal.value.map(item => {
                    return {
                      aspOrderId: item.id,
                      projectId: item.projectId
                    };
                  })
                });
              }}
            >
              下载支付详情
            </BaseButton>
            <BaseButton
              v-resource={[['已完结订单_下载回单']]}
              type="primary2"
              onClick={async () => {
                if (!selectionVal.value.length) {
                  XrkMessage.error('请选择一条数据');
                  return;
                }
                //批量下载回单
                await batchDownLoadReceipt({
                  downloadType: 503,
                  downloadList: selectionVal.value.map(item => {
                    return {
                      id: item.id,
                      aspOrderId: item.id,
                      orderType: item.orderType
                    };
                  })
                });
              }}
            >
              下载回单
            </BaseButton>
          </>
        );
      }
    }
  ]
});

// 列表
const tableConfig = computed<XrkTableProps['tableConfig']>(() => {
  return {
    tableColumns: [
      useTableColumn({
        type: '_selection',
        prop: 'idAndOrderType',
        align: 'center',
        fixed: 'left',
        width: 120
      }),
      useTableColumn({
        type: 'index',
        label: '序号',
        columnKey: 'idAndOrderType'
      }),
      orderNoCol,
      projectNameCol,
      projectNoCol,
      taxCollectionPatternCol,
      entpNameCol,
      taxType.value === 1 && supplierNameCol,
      orderTimeCol,
      orderFinishTimeCol,
      orderTypeCol,
      payTypeCol,
      payPassagewayCol,
      orderPriceCol,
      route.meta.taxType === 2 && deductIndividualTaxAmountCol,
      route.meta.taxType === 2 && paidOrderAmountCol,
      useTableColumn({
        align: 'center',
        fixed: 'right',
        label: '操作',
        width: 270,
        render({ row }) {
          const pathMapDetailB = {
            1: '/main/asp/finished-order/detail-b',
            2: '/main/asp/finished-labor-order/detail-b'
          };
          const pathMapDetailC = {
            1: '/main/asp/finished-order/detail-c',
            2: '/main/asp/finished-labor-order/detail-c'
          };
          const pathMapDetailS = {
            1: '/main/asp/finished-order/detail-s',
            2: '/main/asp/finished-labor-order/detail-s'
          };
          const isSupplierOrder = row.projectStructureType === 1;
          const path = isSupplierOrder
            ? pathMapDetailB[taxType.value]
            : pathMapDetailC[taxType.value];
          return (
            <>
              <div>
                <BaseButton
                  v-resource={[['已完结订单_查看明细']]}
                  onClick={async () => {
                    // 查看明细
                    if (row.orderType === 1) {
                      //项目费用查看明细
                      const routeData = router.resolve({
                        path,
                        query: {
                          sceneType: row.sceneType,
                          aspOrderId: row.id,
                          projectStructureType: row.projectStructureType,
                          isEdit: 0
                        }
                      });
                      window.open(routeData.href);
                    } else {
                      // 商务/平台服务费查看明细
                      const routeData = router.resolve({
                        path: pathMapDetailS[taxType.value],
                        query: {
                          orderId: row.id,
                          orderType: row.orderType,
                          gysx:
                            row.industryId && +row.industryId > 0
                              ? +!row?.isVirtualIndustry
                              : 0,
                          taxPattern: row.taxPattern
                        }
                      });
                      window.open(routeData.href);
                    }
                  }}
                  type="text"
                >
                  查看明细
                </BaseButton>

                {
                  // 项目费用才有下载支付详情
                  row.orderType === 1 && (
                    <BaseButton
                      v-resource={[['已完结订单_下载支付详情']]}
                      onClick={async () => {
                        // 下载支付详情
                        downLoadOrderPayDetail({
                          payPassagewayCode: row.payPassagewayCode,
                          aspOrderIdList: [row.id]
                        });
                      }}
                      type="text"
                    >
                      下载支付详情
                    </BaseButton>
                  )
                }

                <BaseButton
                  v-resource={[['已完结订单_下载回单']]}
                  disabled={!row.isExistReceiptUrl}
                  onClick={async () => {
                    if (row.orderType === 1) {
                      await batchDownLoadReceipt({
                        downloadType: 503,
                        downloadList: [
                          {
                            id: row.id,
                            aspOrderId: row.id,
                            orderType: row.orderType
                          }
                        ]
                      });
                    }
                    // 下载回单
                    downLoadReceipt(row.receiptUrl);
                  }}
                  type="text"
                >
                  下载回单
                </BaseButton>
              </div>
            </>
          );
        }
      })
    ].filter(Boolean),
    baseConfig: { rowKey: 'idAndOrderType' }
  };
});
const searchedParams = ref({ payPassagewayCode: '', orderType: '' });
const getListAjax = async params => {
  const [orderStartTime, orderEndTime] = params.orderTime || ['', ''];
  const [orderFinishTimeStart, orderFinishTimeEnd] = params.orderFinishTime || [
    '',
    ''
  ];

  const reqData = {
    ...params,
    orderStartTime,
    orderEndTime,
    orderFinishTimeStart,
    orderFinishTimeEnd
  };
  const data = await payedOrderPageList(reqData);
  searchedParams.value = reqData;
  return data;
};
</script>
<style lang="scss"></style>
